// @ts-nocheck

import dom from "./dom2.js";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const dontTreeShakeDom = dom;
import CommentMenuPanel, {DummyTextarea, updateCitationButton} from "./components/CommentMenuPanel.jsx";
import ShadowHost from "./common/components/ShadowElements.jsx";
import {copiedConfirmationMsg} from "./common/components/CopyButton.jsx";
import RichTextEditor from "./common/rich-text-editor.js";
import {RTE_WRAPPER_SELECTOR} from "../../main/index.js";
import tippy, {createSingletonWrapper, hideAll} from "./common/tippy-control.js";
// import BgComm from "./common/bg-comm"; // TODO
import Data from "../../main/data/index.js";
import {getHighlightToggle} from "../../main/utils/highlight.js";

export const COMMENT_MENU_JS_SELECTOR = ".js-pn-comment-menu";

class CommentMenu {

	//
	// PUBLIC FUNCTIONS
	//

	/**
	 * @param {Object}   params
	 * @param {Object}   params.data
	 * @param {Object}   params.data.context
	 * @param {Store}    params.data.store
	 * @param {Object}   params.hooks
	 * @param {function} params.hooks.onCitationClick
	 * @param {function} params.hooks.showMessageOnSidebarButton
	 * @param {function} params.hooks.showNewAnnotationAnimation
	 * @param {function} params.hooks.updateAnnotationTopic
	 */
	constructor(params) {
		this.context = params.data.context; // sloppy integration until refactor is complete

		this.openCitation = params.hooks.onCitationClick;
		this.showMessageOnSidebarButton = params.hooks.showMessageOnSidebarButton;
		this.showNewAnnotationAnimation = params.hooks.showNewAnnotationAnimation;
		this.updateAnnotationTopic = params.hooks.updateAnnotationTopic;

		// So javascript plays nicely...
		this.onMouseUpHandler = this.onMouseUp.bind(this);

		this.richTextEditor = null;

		this.tippyInstances = [];
		this.tippySingleton = null;
	}

	/**
	 * Displays comments menu.
	 *
	 * @param {number}  top
	 * @param {number}  left
	 * @param {Object}  annotation
	 * @param {boolean} isNew
	 * @param {boolean} editable
	 */
	async renderCommentsMenu(top, left, annotation, isNew, editable) {
		// Remove any menu that might be open.
		this.context.projectTopicMenu.destroy();
		this.removeCommentsMenu();

		const annotationUuid = annotation.uuid;
		const annotationText = annotation.selectedText;
		const topic = Data.topic.byUuid(Data.outline.getTopicUuidForAnnotation(annotationUuid, Data.app.project?.uuid));

		const isSidebarApiClosed = false;
		// const isSidebarApiClosed = getIsUsingSidebarApi()
		// 	? await BgComm.getIsSidebarApiClosed()
		// 	: false;

		// Construct comment menu panel
		const commentMenuPanel = dom(CommentMenuPanel, {
			annotation,
			editable,
			top,
			left,
			isSaving:           isNew,
			isSidebarApiClosed: isSidebarApiClosed,
			onClickCitation:    () => this.handleCitationClick(annotationUuid, editable),
			onClickCopy:        () => this.copyToClipboard(annotationText),
			onClickDelete:      async () => {await this.handleDeleteAnnotation(annotationUuid);},
			onClickHeader:      this.handleClickOnHeader.bind(this),
			onClickSave:        async () => this.handleSaveClick(editable, annotationUuid, true),
			onKeyDownHeader:    this.handleKeyDownOnHeader.bind(this),
			topic
		});

		await new Promise((resolve, reject) => {
			const onStylesLoaded = (shadow) => {
				shadow.append(commentMenuPanel);
				resolve();
			};

			// Set up shadow host for comment menu
			const commentMenuShadowHost = dom(ShadowHost, {
				classId:                COMMENT_MENU_JS_SELECTOR.substr(1),
				"data-annotation-uuid": annotationUuid,
				"data-is-new":          isNew ? "true" : "false",
				stylesheet:             "comment-menu.css",
				onStylesLoaded
			});
			// Add to DOM so styles can start loading
			document.body.append(commentMenuShadowHost);
		});
		// Add to DOM so styles can start loading
		const wrapper = commentMenuPanel.querySelector(RTE_WRAPPER_SELECTOR);

		const id = "comment-menu";
		this.richTextEditor = new RichTextEditor({
			editable,
			id,
			wrapper,
			blurOnEnter: true,
			content:     annotation.comment,
			onBlur:      ({editor, event}) => this.onBlur({editor, event}, annotationUuid, editable),
			placeholder: editable ? "Enter your notes here..." : "No notes"
		});

		document.body.addEventListener("mouseup", this.onMouseUpHandler);

		if (editable) {
			setTimeout(() => {
				this.richTextEditor.focus();
			}, 50);
		}

		// Add tooltips. Use a singleton so that they'll never overlap each other
		this.tippyInstances = tippy(commentMenuPanel.querySelectorAll("[data-tippy-content]"));
		this.tippySingleton = createSingletonWrapper(this.tippyInstances, {placement: "bottom"});
	}

	/**
	 * This function should be called when a new annotation is saved. It's used to unlock the buttons in the popup
	 * and update the appearance of the citation button.
	 *
	 * @param {Object} annotation
	 */
	newAnnotationSaved(annotation) {
		const annotationUuid = annotation.uuid;
		const popup = document.querySelector(`${COMMENT_MENU_JS_SELECTOR}[data-annotation-uuid="${annotationUuid}"]`).shadowRoot.querySelector('[data-is-saving="true"]');
		if (popup) {
			// Unlock the "save" functionality
			popup.querySelectorAll("button[disabled]").forEach(button => {button.disabled = false;});
			popup.setAttribute("data-is-saving", "false");

			// Make sure the citation icon is shown correctly now that we have data
			const editable = true; // always true for a new annotation
			const onClickCitation = () => this.handleCitationClick(annotationUuid, editable);
			updateCitationButton(annotation, editable, onClickCitation);

			// Redo tooltips
			this.tippyInstances.forEach(inst => inst.destroy());
			this.tippyInstances = tippy(popup.querySelectorAll("[data-tippy-content]"));
			this.tippySingleton.setInstances(this.tippyInstances);
		}
	}

	/**
	 * Destroys the comment menu and its event listeners.
	 */
	destroy() {
		this.removeCommentsMenu();
	}

	//
	// PRIVATE FUNCTIONS
	//

	/**
	 * Remove the notes popup from the DOM.
	 *
	 * Note that this is called when destroy() is called,
	 * regardless of whether the menu actually exists
	 * (for example, when changing projects).
	 */
	removeCommentsMenu() {
		const menu = document.querySelector(COMMENT_MENU_JS_SELECTOR);
		if (!menu)
			return;

		// javascript magically transforms data-is-new to element.dataset.isNew
		const isNew = menu.dataset.isNew === "true";

		menu.remove();
		document.body.removeEventListener("mouseup", this.onMouseUpHandler);

		// show animation on sidebar button if adding a new annotation
		// (visible if sidebar is minimized)
		if (isNew)
			this.showNewAnnotationAnimation();
	}

	/**
	 * Dismiss the notes popup if user clicks outside it.
	 * Save the notes before removing the popup from the dom.
	 *
	 * @param {Object} evt
	 */
	async onMouseUp(evt) {
		if (this.isSaving())
			return;

		const isNotesPopupClick = (evt.target.closest(COMMENT_MENU_JS_SELECTOR) !== null);

		if (!isNotesPopupClick) {
			const popup = document.querySelector(COMMENT_MENU_JS_SELECTOR);
			if (!popup)
				return; // just in case

			const editable = popup.shadowRoot.querySelector(".editable");
			if (editable) {
				// javascript magically transforms data-annotation-uuid to element.dataset.annotationUuid
				const annotationUuid = popup.dataset.annotationUuid;

				// noinspection ES6MissingAwait - no need to wait for this to finish
				this.handleSave(annotationUuid);
			}

			this.destroy();
		}
	}

	/**
	 * If clicked outside of comment menu, save the comment
	 *
	 * @param {Object} editor*
	 * @param {Object} event*
	 * @param {string} annotationUuid
	 */
	async onBlur({editor, event}, annotationUuid, editable) {
		// Hide all tooltips
		hideAll({duration: 0});

		if (!editable) {
			this.destroy();
			return;
		}

		// If new focus target is in the comment menu, don't continue
		if (event.relatedTarget?.closest("#comment-menu"))
			return;

		this.handleSave(annotationUuid, true);
	}

	/**
	 * Copies the provided text to the user's clipboard.
	 *
	 * @param {string} text
	 */
	copyToClipboard(text) {
		// The `text` attribute comes from the annotation when the notes popup is rendered.
		// We don't have a great way to include the citation, so we just copy annotation.selectedText.

		const dta = dom(DummyTextarea, {text});
		document.body.append(dta);

		dta.select();
		document.execCommand("copy");

		dta.remove();
		this.showMessageOnSidebarButton(copiedConfirmationMsg);
	}

	/**
	 * Event handler for user request to delete an annotation.
	 * @see sister function in project-topic-menu.js
	 *
	 * @param {string} annotationUuid
	 * @return {Promise}
	 */
	async handleDeleteAnnotation(annotationUuid) {
		if (!Data.app.project)
			throw new Error();
		if (this.isSaving())
			return;
		this.destroy();
		await Data.annotation.delete(annotationUuid, Data.app.project.uuid);
	}

	/**
	 * Event handler for user request to open the citation panel in the sidebar.
	 * Also saves the comment and closes the comment menu.
	 *
	 * @param {string} annotationUuid
	 * @return {Promise}
	 */
	async handleCitationClick(annotationUuid, editable) {
		if (this.isSaving())
			return;

		this.handleSaveClick(editable, annotationUuid);
		this.openCitation(annotationUuid);
	}

	async handleSaveClick(editable, annotationUuid, returnFocusToPage = false) {
		// Close menu without doing anything
		if (!editable) {
			this.destroy();
			return;
		}

		this.handleSave(annotationUuid, returnFocusToPage);
	}

	/**
	 * Save notes, then close the popup.
	 *
	 * @param {string} annotationUuid
	 */
	async handleSave(annotationUuid, returnFocusToPage = false) {
		if (this.isSaving())
			return;

		// Move keyboard focus to rendered annotation
		if (returnFocusToPage) {
			// Use a timeout because the rendered highlight seems to update (a
			// few?) times, causing the focus to disappear if it's set too early
			setTimeout(() => {
				const highlightToggle = getHighlightToggle(annotationUuid);
				highlightToggle?.focus();
			}, 300);
		}

		const notes = this.richTextEditor.getEditorContentHTML();
		// TODO: Referenced in comment from main js file. We probably should make this a callback rather than using context
		this.context.updateAnnotationComment(annotationUuid, notes);
		this.destroy();
	}

	/**
	 * Clicking on the notes modal ("CommentMenu") header opens the topic menu,
	 * allowing the user to change the topic for the highlight.
	 *
	 * @param {Object} evt
	 */
	handleClickOnHeader(evt) {
		this.renderProjectTopicMenu(evt, false);
	}

	handleKeyDownOnHeader(event) {
		event.stopPropagation();
		event.preventDefault();

		this.renderProjectTopicMenu(event, true);
	}

	renderProjectTopicMenu(event, isKeyboard) {
		if (this.isSaving())
			return;

		const menu = event.target.getRootNode().host;
		if (!menu)
			return;

		// javascript magically transforms data-annotation-uuid to element.dataset.annotationUuid
		const annotationUuid = menu.dataset.annotationUuid;
		if (!annotationUuid)
			return;

		const shadowMenu = document.querySelector(COMMENT_MENU_JS_SELECTOR).shadowRoot.querySelector("#comment-menu");
		const top = shadowMenu.style.top;
		const left = shadowMenu.style.left;

		this.context.projectTopicMenu.renderMenuAt(top, left, annotationUuid, isKeyboard, (topicUuid) => {
			this.context.updateAnnotationTopic(annotationUuid, topicUuid);
		});
	}

	/**
	 * Returns true if the annotation is being saved.
	 * This typically happens when generating the citation for a new annotation takes a long time.
	 *
	 * @return {boolean}
	 */
	isSaving() {
		return !!document.querySelector(COMMENT_MENU_JS_SELECTOR).shadowRoot.querySelector('[data-is-saving="true"]');
	}
}

export default CommentMenu;
